import { Link } from "react-router-dom";
import { LogOut, Menu } from "lucide-react";
import { useState } from "react";
import Logo from "../layout/Logo";

function Nav({ nav, active, onChange, close }) {
  return (
    <nav className="space-y-1">
      {nav.map((n) => (
        <button key={n.key} onClick={() => { onChange(n.key); close?.(); }} data-testid={`panel-nav-${n.key}`}
          className={`flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-left text-sm font-medium transition-colors ${active === n.key ? "bg-white/10 text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}>
          <n.icon size={17} className={active === n.key ? "text-gold" : ""} />
          <span className="flex-1">{n.label}</span>
          {n.badge > 0 && <span className="rounded-full bg-mint px-2 py-0.5 text-[10px] font-bold text-navy">{n.badge}</span>}
        </button>
      ))}
    </nav>
  );
}

export default function PanelShell({ label, nav, active, onChange, user, onLogout, children }) {
  const [open, setOpen] = useState(false);
  const current = nav.find((n) => n.key === active);
  const side = (
    <div className="flex h-full flex-col p-5">
      <Link to="/" data-testid="panel-home-link"><Logo /></Link>
      <p className="mb-6 mt-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-gold">{label}</p>
      <Nav nav={nav} active={active} onChange={onChange} close={() => setOpen(false)} />
      <div className="mt-auto border-t border-white/10 pt-5">
        <p className="truncate text-sm font-semibold text-white" data-testid="panel-user-name">{user?.name}</p>
        <p className="truncate text-xs text-slate-500">{user?.email}</p>
        <button onClick={onLogout} data-testid="panel-logout-btn" className="mt-4 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white"><LogOut size={15} /> Sign out</button>
      </div>
    </div>
  );
  return (
    <div className="min-h-screen bg-slate-100">
      <aside className="fixed inset-y-0 left-0 hidden w-64 bg-navy lg:block">{side}</aside>
      {open && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setOpen(false)}><aside className="h-full w-64 bg-navy" onClick={(e) => e.stopPropagation()}>{side}</aside></div>}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-slate-200 bg-white/80 px-5 backdrop-blur-xl sm:px-8">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="panel-mobile-menu-btn" aria-label="Menu"><Menu /></button>
          <h1 className="text-lg font-bold text-navy" data-testid="panel-title">{current?.label}</h1>
        </header>
        <main className="p-5 sm:p-8">{children}</main>
      </div>
    </div>
  );
}

export function Card({ children, className = "", ...rest }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-6 ${className}`} {...rest}>{children}</div>;
}

export function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <Card className="flex items-center gap-4">
      {Icon && <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-gold"><Icon size={19} /></div>}
      <div><p className="text-xs uppercase tracking-wider text-slate-500">{label}</p><p className="font-num text-2xl font-semibold text-navy" data-testid={testid}>{value}</p></div>
    </Card>
  );
}

const BADGE = {
  new: "bg-blue-50 text-blue-700", contacted: "bg-amber-50 text-amber-700", follow_up: "bg-purple-50 text-purple-700",
  converted: "bg-emerald-50 text-emerald-700", closed: "bg-slate-100 text-slate-600", open: "bg-blue-50 text-blue-700",
  in_progress: "bg-amber-50 text-amber-700", resolved: "bg-emerald-50 text-emerald-700",
};

export function StatusBadge({ status, testid }) {
  return <span data-testid={testid} className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${BADGE[status] || "bg-slate-100 text-slate-600"}`}>{(status || "").replace("_", " ")}</span>;
}

export function Empty({ text, testid }) {
  return <p data-testid={testid} className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500">{text}</p>;
}
